// Umbrales con los que se interpretan las lecturas de una estación. Los
// guarda el backend por estación (ver backend/src/domain/entities/Thresholds.ts);
// acá están los tipos y cómo se presentan en el formulario.
export interface StationThresholds {
  spray: { deltaTMin: number; deltaTMax: number; deltaTLimit: number; windMin: number; windMax: number; windLimit: number; rainFreeHours: number };
  frost: { riskC: number; frostC: number };
  airHumidity: { highPct: number; lowPct: number };
  soil: { dryPct: number; lowPct: number; saturatedPct: number };
}

export type ThresholdSection = keyof StationThresholds;

export interface ThresholdField {
  key: string;
  label: string;
  unit: string;
  step: number;
}

export interface ThresholdSectionInfo {
  section: ThresholdSection;
  title: string;
  description: string;
  // Qué componente tiene que tener la estación para que la sección aplique.
  requires: 'air' | 'air+wind' | 'soil';
  fields: ThresholdField[];
}

export const THRESHOLD_SECTIONS: ThresholdSectionInfo[] = [
  {
    section: 'spray',
    title: 'Ventana de pulverización',
    description:
      'Delta T y viento entre el mínimo y el máximo ideal son buenas condiciones. Por encima del límite, no conviene. Con viento por debajo del mínimo puede haber inversión térmica. Si el pronóstico trae lluvia antes de las horas sin lluvia, el producto se puede lavar.',
    requires: 'air+wind',
    fields: [
      { key: 'deltaTMin', label: 'Delta T mínimo ideal', unit: '', step: 0.5 },
      { key: 'deltaTMax', label: 'Delta T máximo ideal', unit: '', step: 0.5 },
      { key: 'deltaTLimit', label: 'Delta T límite (no conviene)', unit: '', step: 0.5 },
      { key: 'windMin', label: 'Viento mínimo ideal', unit: 'km/h', step: 1 },
      { key: 'windMax', label: 'Viento máximo ideal', unit: 'km/h', step: 1 },
      { key: 'windLimit', label: 'Viento límite (no conviene)', unit: 'km/h', step: 1 },
      { key: 'rainFreeHours', label: 'Horas sin lluvia después de aplicar', unit: 'h', step: 1 },
    ],
  },
  {
    section: 'frost',
    title: 'Helada',
    description:
      'La temperatura de la estación (a 1,5 m) es más alta que a ras del cultivo: con unos grados arriba de cero ya puede helar abajo.',
    requires: 'air',
    fields: [
      { key: 'riskC', label: 'Avisar riesgo con mínima de', unit: '°C', step: 0.5 },
      { key: 'frostC', label: 'Considerar helada con', unit: '°C', step: 0.5 },
    ],
  },
  {
    section: 'airHumidity',
    title: 'Humedad del aire',
    description: 'Humedad alta sostenida favorece hongos y mojado de hoja; muy baja, más evaporación y estrés hídrico.',
    requires: 'air',
    fields: [
      { key: 'highPct', label: 'Humedad alta desde', unit: '%', step: 1 },
      { key: 'lowPct', label: 'Aire muy seco por debajo de', unit: '%', step: 1 },
    ],
  },
  {
    section: 'soil',
    title: 'Humedad de suelo',
    description: 'Depende del tipo de suelo y del cultivo: ajustalo a lo que ves en tu lote.',
    requires: 'soil',
    fields: [
      { key: 'dryPct', label: 'Seco por debajo de', unit: '%', step: 1 },
      { key: 'lowPct', label: 'Bajando por debajo de', unit: '%', step: 1 },
      { key: 'saturatedPct', label: 'Saturado por encima de', unit: '%', step: 1 },
    ],
  },
];

export function thresholdValue(t: StationThresholds, section: ThresholdSection, key: string): number {
  return (t[section] as Record<string, number>)[key];
}
